import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const IndustryExpertiseSection = () => {
  const industries = [
  {
    id: 1,
    icon: "UtensilsCrossed",
    title: "Restaurant & Food Service",
    image: "https://images.unsplash.com/photo-1721924960863-f241e9f1a9ac",
    imageAlt: "Busy commercial restaurant kitchen with chefs in white uniforms working at stainless steel cooking stations, bright overhead lighting, and professional food preparation equipment",
    description: "Specialized expertise in commercial kitchen plumbing and electrical systems for high-volume food service operations.",
    services: [
    "Commercial kitchen installations",
    "Grease trap systems",
    "High-capacity water heaters",
    "Ventilation integration",
    "Emergency response protocols"],

    clients: ["McDonald\'s", "KFC", "Independent Restaurants"]
  },
  {
    id: 2,
    icon: "ShoppingBag",
    title: "Retail & Shopping Centers",
    image: "https://images.unsplash.com/photo-1713480198248-3a1cf4b88e9a",
    imageAlt: "Modern shopping mall interior with multiple retail stores, bright LED lighting, polished floors, glass storefronts, and shoppers browsing merchandise displays",
    description: "Comprehensive infrastructure solutions for retail environments requiring reliability and minimal disruption.",
    services: [
    "Multi-tenant plumbing systems",
    "Energy-efficient lighting",
    "Restroom facilities",
    "Water management systems",
    "Scheduled maintenance programs"],

    clients: ["Shopping Malls", "Retail Chains", "Boutique Stores"]
  },
  {
    id: 3,
    icon: "Building",
    title: "Office & Corporate",
    image: "https://images.unsplash.com/photo-1656646425073-a1ec4a84e0dc",
    imageAlt: "Contemporary corporate office building with glass curtain walls, modern steel architecture, landscaped entrance, and professional business environment exterior view",
    description: "Professional-grade electrical and plumbing solutions designed for modern office environments.",
    services: [
    "Smart building integration",
    "LED lighting retrofits",
    "Water efficiency systems",
    "Emergency power systems",
    "Preventive maintenance"],

    clients: ["Corporate Offices", "Business Parks", "Co-working Spaces"]
  },
  {
    id: 4,
    icon: "Factory",
    title: "Industrial & Manufacturing",
    image: "https://images.unsplash.com/photo-1670483111819-ed62eff77173",
    imageAlt: "Large industrial manufacturing facility with heavy machinery, overhead crane systems, metal piping infrastructure, and workers in safety gear on production floor",
    description: "Heavy-duty industrial solutions for manufacturing facilities with demanding operational requirements.",
    services: [
    "High-pressure plumbing systems",
    "Industrial electrical installations",
    "Process water systems",
    "Equipment integration",
    "24/7 support contracts"],

    clients: ["Manufacturing Plants", "Warehouses", "Distribution Centers"]
  }];


  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Target" size={20} color="var(--color-primary)" />
            <span className="text-primary font-semibold text-sm font-accent">Industry Expertise</span>
          </div>
          <h2 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Specialized Solutions by Industry
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto font-body">
            Deep industry knowledge and tailored approaches for diverse commercial sectors, each with unique requirements and challenges.
          </p>
        </div>

        <div className="space-y-8 lg:space-y-12">
          {industries?.map((industry, index) =>
          <div
            key={industry?.id}
            className={`bg-card rounded-2xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden ${
            index % 2 === 0 ? '' : 'lg:flex-row-reverse'}`
            }>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className={`relative h-64 md:h-80 lg:h-auto overflow-hidden ${index % 2 === 0 ? '' : 'lg:order-2'}`}>
                  <Image
                  src={industry?.image}
                  alt={industry?.imageAlt}
                  className="w-full h-full object-cover" />

                  <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent lg:bg-gradient-to-r"></div>
                </div>

                <div className={`p-6 lg:p-10 ${index % 2 === 0 ? '' : 'lg:order-1'}`}>
                  <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                    <Icon name={industry?.icon} size={28} color="var(--color-primary)" />
                  </div>

                  <h3 className="font-headline font-bold text-2xl md:text-3xl text-foreground mb-4">
                    {industry?.title}
                  </h3>

                  <p className="text-muted-foreground mb-6 font-body leading-relaxed text-lg">
                    {industry?.description}
                  </p>

                  <div className="mb-6">
                    <p className="font-semibold text-foreground mb-3 font-headline">Specialized Services:</p>
                    <div className="space-y-2">
                      {industry?.services?.map((service, idx) =>
                    <div key={idx} className="flex items-start gap-2">
                          <Icon name="CheckCircle2" size={18} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span className="text-foreground font-body">{service}</span>
                        </div>
                    )}
                    </div>
                  </div>

                  <div className="pt-6 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-2 font-body">Trusted by:</p>
                    <div className="flex flex-wrap gap-2">
                      {industry?.clients?.map((client, idx) =>
                    <span
                      key={idx}
                      className="px-3 py-1 bg-muted text-foreground text-sm rounded-full font-body">

                          {client}
                        </span>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 lg:mt-16 text-center bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 lg:p-12 text-white">
          <h3 className="font-headline font-bold text-2xl md:text-3xl mb-4">
            Don't See Your Industry Listed?
          </h3>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto font-body">
            We've worked across diverse commercial sectors for over 30 years. Contact us to discuss your specific industry requirements and how we can help.
          </p>
          <a
            href="tel:0827935785"
            className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 rounded-lg font-semibold text-lg hover:bg-accent/90 transition-all emergency-cta">

            <Icon name="Phone" size={24} />
            <span className="font-accent">082 793 5785</span>
          </a>
        </div>
      </div>
    </section>);

};

export default IndustryExpertiseSection;